import type { SftpConnectorDTO } from '@today/types'
import { Button } from '@today/ui-primitives'
import { useId } from 'react'
import type { FieldErrors } from '../file-exchange-file-form/form-fields'
import { SftpConnectionCard } from './sftp-connection-card'
import { type SftpConnectionDraft, SftpConnectionForm } from './sftp-connection-form'

export type { SftpConnectionDraft }

/**
 * Props for {@link SftpConnectionsPanel}.
 *
 * @property connectors - The tenant's SFTP connections, oldest first.
 * @property adding - Whether the add form is open (the caller closes it after a stored add).
 * @property busy - A request is in flight; actions are disabled.
 * @property addErrors - Field path → message for a refused add.
 * @property addError - A form-level message for a refused add.
 * @property connectorErrors - Connector id → the message of its last refused action.
 */
export interface SftpConnectionsPanelProps {
	connectors: readonly SftpConnectorDTO[]
	adding: boolean
	busy?: boolean
	addErrors?: FieldErrors
	addError?: string | null
	connectorErrors?: Readonly<Record<string, string>>
	onAdd: (draft: SftpConnectionDraft) => void
	onAddOpenChange: (open: boolean) => void
	onTest: (connectorId: string) => void
	onTrust: (connectorId: string, fingerprint: string) => void
	onDelete: (connectorId: string) => void
	onCopy: (text: string) => void
}

/**
 * A tenant's SFTP connections (TO-393 REQ-014), for a Super-Admin: add a
 * connection, copy its public key and egress IPs, test it, trust its host key,
 * delete it. Pure: connections, errors and actions are props.
 */
export function SftpConnectionsPanel({
	connectors,
	adding,
	busy = false,
	addErrors = {},
	addError = null,
	connectorErrors = {},
	onAdd,
	onAddOpenChange,
	...actions
}: SftpConnectionsPanelProps) {
	const headingId = useId()
	return (
		<section aria-labelledby={headingId} className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<h3 id={headingId} className="font-ds-heading text-ds-lg text-ds-text">
					SFTP connections
				</h3>
				{adding ? null : (
					<Button size="sm" isDisabled={busy} onPress={() => onAddOpenChange(true)}>
						Add connection
					</Button>
				)}
			</div>
			{adding ? (
				<SftpConnectionForm
					busy={busy}
					errors={addErrors}
					error={addError}
					onSubmit={onAdd}
					onCancel={() => onAddOpenChange(false)}
				/>
			) : null}
			{connectors.length === 0 && !adding ? (
				<p className="text-ds-sm text-ds-text-muted">No SFTP connections yet.</p>
			) : null}
			{connectors.map((connector) => (
				<SftpConnectionCard
					key={connector.id}
					connector={connector}
					busy={busy}
					error={connectorErrors[connector.id]}
					{...actions}
				/>
			))}
		</section>
	)
}
